import { useState } from 'react';
import { css } from '@bts-web/utils-style-engine';
import {
  ButtonBase,
  CheckboxToggle,
  DocumentationExternalLink,
} from '@bts-web/core-features/common';
import { links } from '@bts-web/core-app-config';
import {
  CommonConsentScreenProps,
  NonEssentialCookiesConsent,
  paragraphStyles,
  titleStyles,
  linkStyles,
} from '../CookiesConsentBanner/CookiesConsentBanner';
import {
  FilledArrowLeft,
  RegularCookies,
  RegularDataBarVertical,
  RegularWindow,
} from '../../common/svgIcons';

interface ManageConsentProps extends CommonConsentScreenProps {
  cookiePreferences: NonEssentialCookiesConsent;
  onClickBackToMain: () => void;
}

const consentItemStyles = css({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'space-between',
  paddingY: 'medium',
  borderBottom: '1px solid',
  borderBottomColor: 'neutrals.fill_quaternary',
});

const consentItemTitleStyles = css({
  display: 'flex',
  gap: 'extra_small_2',
  alignItems: 'center',
  color: 'neutrals.text_primary',
  fontFamily: 'label.medium_bold',
  fontSize: 'label.medium_bold',
  fontWeight: 'label.medium_bold',
  lineHeight: 'label.medium_bold',
  letterSpacing: 'label.medium_bold',
});

const ManageConsent = ({
  cookiePreferences,
  translations,
  updateCookiePreferences,
  onClickBackToMain,
  language,
}: ManageConsentProps) => {
  const [currentPreferences, setCurrentPreferences] =
    useState<NonEssentialCookiesConsent>(cookiePreferences);

  const handleConfirmPreferences = () => {
    updateCookiePreferences(currentPreferences);
  };

  const handleAcceptAll = () => {
    setCurrentPreferences({
      performance: true,
      marketing: true,
    });
  };

  return (
    <>
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: 'small',
        })}
      >
        <div
          className={css({
            display: 'flex',
            gap: 'small',
            alignItems: 'center',
          })}
        >
          <ButtonBase
            size="small"
            onClick={onClickBackToMain}
            noPadding
            visual="ghost"
          >
            <FilledArrowLeft size={24} data-dir="rtl" />
          </ButtonBase>
          <h2
            className={`${titleStyles} ${css({
              width: '100%',
              marginRight: 'large',
            })}`}
          >
            {translations.cookiesConsentManage}
          </h2>
        </div>
        <p
          className={`${paragraphStyles} ${
            language === 'ar'
              ? css({
                  fontSize: 'title.large',
                  fontFamily: 'title.large',
                  fontWeight: 'title.large',
                  lineHeight: 'title.large',
                  letterSpacing: 'title.large',
                })
              : ''
          }`}
        >
          {translations.cookiesConsentManageDescription}{' '}
          <DocumentationExternalLink
            className={linkStyles}
            href={links.cookiePolicy}
            label={translations.cookiePolicy}
            visual="primaryUnderline"
            size="small"
          />
          .
        </p>
      </div>
      <div>
        <span
          className={css({
            color: 'neutrals.text_secondary',
            fontFamily: 'body.medium',
            fontSize: 'body.medium',
            fontWeight: 'body.medium',
            lineHeight: 'body.medium',
            letterSpacing: 'body.medium',
          })}
        >
          {translations.cookiesConsentManageLabel}
        </span>
        <div className={consentItemStyles}>
          <div className={consentItemTitleStyles}>
            <RegularCookies size={24} />
            <span>{translations.cookiesConsentEssentialCookies}</span>
          </div>
          <span
            className={css({
              color: 'positive.text_primary',
              fontFamily: 'label.medium_bold',
              fontSize: 'label.medium_bold',
              fontWeight: 'label.medium_bold',
              lineHeight: 'label.medium_bold',
              letterSpacing: 'label.medium_bold',
            })}
          >
            {translations.cookiesConsentAlwaysActive}
          </span>
        </div>
        <div className={consentItemStyles}>
          <div className={consentItemTitleStyles}>
            <RegularDataBarVertical size={24} />
            <span>{translations.cookiesConsentPerformanceCookies}</span>
          </div>

          <CheckboxToggle
            testId="performance-toggle"
            variant="positive"
            checked={currentPreferences.performance}
            onChange={() => {
              setCurrentPreferences((prev) => ({
                ...prev,
                performance: !prev.performance,
              }));
            }}
          />
        </div>
        <div className={consentItemStyles}>
          <div className={consentItemTitleStyles}>
            <RegularWindow size={24} />
            <span>{translations.cookiesConsentAdvertisementCookies}</span>
          </div>
          <CheckboxToggle
            testId="marketing-toggle"
            variant="positive"
            checked={currentPreferences.marketing}
            onChange={() => {
              setCurrentPreferences((prev) => ({
                ...prev,
                marketing: !prev.marketing,
              }));
            }}
          />
        </div>
      </div>
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: 'small',
        })}
      >
        <ButtonBase
          fullWidth
          visual="primary"
          onClick={handleConfirmPreferences}
        >
          {translations.cookiesConsentConfirmPreferences}
        </ButtonBase>
        <ButtonBase fullWidth visual="secondary" onClick={handleAcceptAll}>
          {translations.cookiesConsentAcceptAll}
        </ButtonBase>
      </div>
    </>
  );
};

export { ManageConsent };
